import { computed, inject, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useSessionStore } from '@/application/auth'
import { hasRoleAtLeast } from '@/domain/auth'
import { TenantError, type Tenant } from '@/domain/tenants'
import { TENANTS_API_KEY } from '@/ports'
import type { TenantsApi } from '@/ports/tenants-api'

import { useActiveTenantStore } from './active-tenant.store'

/**
 * useTenantSwitcher — composable use case du sélecteur de tenant
 * pour le super_admin.
 *
 * Charge la liste des tenants au mount (GET /api/v1/tenants — réservé
 * super_admin par le backend), expose le tenant actif et la fonction de
 * sélection. Pour les autres rôles, retourne `tenants: []` et un
 * `canSwitch: false` qui permet à l'UI de cacher le sélecteur.
 */
export function useTenantSwitcher() {
    const session = useSessionStore()
    const { user } = storeToRefs(session)

    const activeTenantStore = useActiveTenantStore()
    const { activeTenantId, isOverridden } = storeToRefs(activeTenantStore)

    const tenantsApi = injectTenantsApi()
    const tenants = ref<Tenant[]>([])
    const isLoading = ref(false)
    const loadError = ref<string | null>(null)

    const canSwitch = computed(() =>
        user.value !== null && hasRoleAtLeast(user.value.role, 'super_admin'),
    )

    const activeTenant = computed<Tenant | null>(() => {
        if (!activeTenantId.value) return null
        return tenants.value.find((t) => t.id === activeTenantId.value) ?? null
    })

    async function load(): Promise<void> {
        if (!canSwitch.value) {
            tenants.value = []
            return
        }
        isLoading.value = true
        loadError.value = null
        try {
            tenants.value = await tenantsApi.list()
        } catch (err) {
            loadError.value =
                err instanceof TenantError ? err.message : 'Failed to load tenants'
            tenants.value = []
        } finally {
            isLoading.value = false
        }
    }

    function select(tenantId: string | null): void {
        if (tenantId === null) {
            activeTenantStore.clearActive()
        } else {
            activeTenantStore.setActive(tenantId)
        }
    }

    onMounted(() => {
        if (canSwitch.value) {
            void load()
        }
    })

    return {
        canSwitch,
        tenants,
        activeTenantId,
        activeTenant,
        isOverridden,
        isLoading,
        loadError,
        load,
        select,
    }
}

function injectTenantsApi(): TenantsApi {
    const api = inject(TENANTS_API_KEY, null)
    if (!api) {
        throw new TenantError(
            'internal_error',
            'TenantsApi not provided (bootstrap bug)',
        )
    }
    return api
}
